/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useEffect, useState } from "react";
import { MediaSterilization } from "../../../types/mediaBatchSterilization.types";
import { useTranslation } from "react-i18next";
import { SterilizationDetails } from "../../../types/mediaBatchCommon.types";

interface Props {
	sterilizationPayload: MediaSterilization;
	handlePayloadChange: (field: string, value: any) => void;
	maxTemp: string;
	isReadOnly: boolean;
	sterilizationDetails?: SterilizationDetails;
	errors: {
		averagePressure: string;
		holdDuration: string;
		maxTemperature: string;
	};
	setErrors: React.Dispatch<React.SetStateAction<any>>;
}

const MBSObservedParameters = ({
	sterilizationPayload,
	handlePayloadChange,
	maxTemp,
	isReadOnly,
	sterilizationDetails,
	errors,
	setErrors
	
}: Props) => {
	const { t } = useTranslation();
	const [justification, setJustification] = useState("");
	const [isProcessDeviation, setProcessDeviation] = useState(false);
	const maxTemperature = Number(maxTemp.replace("°C", "").trim());

	// Hydrate form fields if data exists in read-only mode
	useEffect(() => {
		if (sterilizationDetails) {
			handlePayloadChange("average_pressure_psi", sterilizationDetails.average_pressure_psi);
			handlePayloadChange("hold_duration_min", sterilizationDetails.hold_duration_min);
			handlePayloadChange("max_temperature_observed", sterilizationDetails.max_temperature_observed);
		}
	}, [sterilizationDetails]);


	const inputClassName = `w-full px-4 py-3 border-2 rounded-xl text-lg font-black outline-none transition-all border-slate-100 text-slate-800 border-none ${
		isReadOnly
			? "bg-slate-100 cursor-not-allowed opacity-70"
			: "bg-white focus:border-orange-500"
	}`;

	return (
		<div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
			<div className="px-6 py-2 border-b border-slate-100 flex items-center gap-2">
				<div className="w-6 h-6 rounded-md bg-[#2D3E50] text-white flex items-center justify-center text-[10px] font-black">
					02
				</div>
				<h3 className="text-[10px] font-black text-[#2D3E50] uppercase tracking-widest mt-2">
					{t("mediaBatchInit.sterilization.observedParameters")}
				</h3>
			</div>
			<div className="p-8">
				<div className="grid grid-cols-12 gap-4">
					<div className="col-span-12 lg:col-span-7 flex flex-col justify-between">
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-1">
								<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
									{t("mediaBatchInit.sterilization.avgPressure")}  <span className="text-red-500">*</span>
								</label>
								<input
									type="number"
									step="0.1"
									placeholder="0.0"
									disabled={isReadOnly}
									value={sterilizationPayload.average_pressure_psi}
									onChange={(e) => { handlePayloadChange("average_pressure_psi", parseFloat(e.target.value)); setErrors((prev: any) => ({ ...prev, averagePressure: "" })); }}
									className={inputClassName}
								/>
								{errors.averagePressure && <p className="text-red-500 text-[11px]">{errors.averagePressure}</p>}
							</div>
							<div className="space-y-1">
								<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
									{t("mediaBatchInit.sterilization.holdDuration")}  <span className="text-red-500">*</span>
								</label>
								<div className="flex items-center gap-3">
									<input
										type="number"
										disabled={isReadOnly}
										placeholder="0.0"
										value={sterilizationPayload.hold_duration_min}
										onChange={(e) => { handlePayloadChange("hold_duration_min", parseInt(e.target.value)); setErrors((prev: any) => ({ ...prev, holdDuration: "" })); }}
										className={inputClassName}
									/>
									<span className="text-[10px] font-black text-slate-300 uppercase">Min</span>
								</div>
								{errors.holdDuration && <p className="text-red-500 text-[11px]">{errors.holdDuration}</p>}
							</div>
						</div>

						<div className="mt-8 p-6 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-around text-center">
							<div>
								<p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
									{t("mediaBatchInit.sterilization.targetTemp")}
								</p>
								<p className="text-xl font-black text-slate-600 tracking-tight">{maxTemp}</p>
							</div>
						</div>
					</div>

					<div className="col-span-12 lg:col-span-5">
						<div className="h-full border-[4px] rounded-[2.5rem] flex flex-col items-center justify-center p-6 border-[#2D3E50]">
							<p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">
								{t("mediaBatchInit.sterilization.maxTempObserved")} <span className="text-red-500">*</span>
							</p>
							<div className="flex items-center justify-center gap-2 w-full">
								<input
									type="number"
									step="0.1"
									disabled={isReadOnly}
									placeholder="0.0"
									value={sterilizationPayload.max_temperature_observed}
									onChange={(e) => { handlePayloadChange("max_temperature_observed", parseFloat(e.target.value)); setErrors((prev: any) => ({ ...prev, maxTemperature: "" })); }}
									className={`w-32 bg-transparent border-none outline-none text-center text-6xl font-black tracking-tighter p-0 focus:ring-0 ${
										isReadOnly
											? "text-slate-400 cursor-not-allowed"
											: isProcessDeviation
												? "text-red-600"
												: "text-[#2D3E50]"
									}`}
								/>
								<span className="text-2xl font-black text-slate-200">°C</span>
							</div>
							{errors.maxTemperature && <p className="text-red-500 text-[11px]">{errors.maxTemperature}</p>}

						</div>
					</div>
				</div>

				{isProcessDeviation && (
					<div className="mt-8 bg-[#FFF5F5] border border-[#FEE2E2] rounded-lg p-6 flex gap-6">
						<div className="shrink-0">
							<div className="w-10 h-10 rounded-full bg-[#FEE2E2] flex items-center justify-center">
								<span className="material-symbols-outlined text-[#B91C1C] font-black text-2xl">warning</span>
							</div>
						</div>
						<div className="flex-1 space-y-4">
							<div>
								<h4 className="text-xs font-black text-[#B91C1C] uppercase tracking-wider mb-1">
									{t("mediaBatchInit.sterilization.manualObservationDeviationDetected")}
								</h4>
								<p className="text-[11px] text-[#991B1B] font-medium leading-relaxed">
									{t("mediaBatchInit.sterilization.manualObservationJustificationText")}
								</p>
							</div>
							<textarea
								value={justification}
								disabled={isReadOnly}
								onChange={(e) => setJustification(e.target.value)}
								placeholder="Enter justification..."
								className="w-full bg-white border border-[#E5E7EB] rounded-lg p-4 text-[11px] font-medium text-slate-600 outline-none min-h-[80px] resize-none focus:ring-2 focus:ring-red-500/10 shadow-sm"
							></textarea>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default MBSObservedParameters;
